import { memo, useCallback, useState } from "react";
import { toast } from "sonner";
import { AlertCircle, Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Spinner } from "@/shared/components/ui/spinner";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { commissionService } from "../services/commission.service";

import { useCommissionList } from "../hooks/useCommissionList";
import type { CommissionListFilters } from "../hooks/useCommissionList";
import type {
  CommissionResponseDTO,
  CommissionUpsertRequestDTO,
} from "../types/commission.types";
import { CommissionFilters } from "./CommissionFilters";
import { CommissionsTable } from "./CommissionsTable";
import { CommissionFormDialog } from "./CommissionFormDialog";
import { CommissionDeleteDialog } from "./CommissionDeleteDialog";

const DIALOG_MODE = {
  FORM: "form",
  DELETE: "delete",
} as const;

type DialogMode = (typeof DIALOG_MODE)[keyof typeof DIALOG_MODE];

interface SellerCommissionsModalProps {
  open: boolean;
  sellerId: number | null;
  sellerName: string;
  initialStartDate: string;
  initialEndDate: string;
  onClose: () => void;
  onMutate: () => void;
}

/**
 * Modal de detalhes de comissões de um vendedor.
 *
 * Banner persistente avisa que ações de criar/editar/excluir são
 * administrativas (comissões são calculadas automaticamente). Carrega
 * comissões individuais do vendedor com período + filtros server-side
 * via `useCommissionList`. Depois de qualquer mutação, chama `onMutate`
 * para que a tela principal refaça o `/summary`.
 *
 * @example
 * <SellerCommissionsModal
 *   open={selectedSeller !== null}
 *   sellerId={selectedSeller?.sellerId ?? null}
 *   sellerName={selectedSeller?.sellerName ?? ''}
 *   initialStartDate={summary.period.startDate}
 *   initialEndDate={summary.period.endDate}
 *   onClose={() => setSelectedSeller(null)}
 *   onMutate={summary.reload}
 * />
 */
export const SellerCommissionsModal = memo(function SellerCommissionsModal({
  open,
  sellerId,
  sellerName,
  initialStartDate,
  initialEndDate,
  onClose,
  onMutate,
}: SellerCommissionsModalProps) {
  const [dialogMode, setDialogMode] = useState<DialogMode | null>(null);
  const [dialogContent, setDialogContent] = useState<DialogMode | null>(null);
  const [selected, setSelected] = useState<CommissionResponseDTO | null>(null);
  const [dialogLoading, setDialogLoading] = useState(false);

  const list = useCommissionList({
    sellerId,
    enabled: open,
    initialStartDate,
    initialEndDate,
  });

  const closeDialog = useCallback(() => {
    setDialogMode(null);
  }, []);

  const handleOpenCreate = useCallback(() => {
    setSelected(null);
    setDialogMode(DIALOG_MODE.FORM);
    setDialogContent(DIALOG_MODE.FORM);
  }, []);

  const handleOpenEdit = useCallback((commission: CommissionResponseDTO) => {
    setSelected(commission);
    setDialogMode(DIALOG_MODE.FORM);
    setDialogContent(DIALOG_MODE.FORM);
  }, []);

  const handleOpenDelete = useCallback((commission: CommissionResponseDTO) => {
    setSelected(commission);
    setDialogMode(DIALOG_MODE.DELETE);
    setDialogContent(DIALOG_MODE.DELETE);
  }, []);

  const handleApplyFilters = useCallback(
    (filters: CommissionListFilters) => {
      list.setFilters(filters);
    },
    [list],
  );

  const handleClearFilters = useCallback(() => {
    list.setFilters({
      startDate: initialStartDate,
      endDate: initialEndDate,
    });
  }, [list, initialStartDate, initialEndDate]);

  const handleFormSubmit = async (data: CommissionUpsertRequestDTO) => {
    setDialogLoading(true);
    try {
      if (selected) {
        const updated = await commissionService.update(selected.id, data);
        list.updateLocal(updated);
        toast.success("Comissão atualizada");
      } else {
        await commissionService.create(data);
        list.reload();
        toast.success("Comissão criada");
      }
      onMutate();
      closeDialog();
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selected) return;
    setDialogLoading(true);
    try {
      await commissionService.remove(selected.id);
      list.removeLocal(selected.id);
      toast.success("Comissão excluída");
      onMutate();
      closeDialog();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, "Erro ao excluir comissão"));
    } finally {
      setDialogLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Comissões de {sellerName}</DialogTitle>
            <DialogDescription>
              Detalhamento das comissões individuais do vendedor no período.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-amber-500/10 rounded-md border border-amber-500/30">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold">Ação administrativa</p>
                <p className="text-sm text-muted-foreground">
                  Comissões são calculadas automaticamente. Criar, editar ou
                  excluir comissões nesta tela são ações administrativas e não
                  fazem parte do fluxo normal de operação.
                </p>
              </div>
            </div>

            <CommissionFilters
              filters={list.filters}
              loading={list.loading}
              onApply={handleApplyFilters}
              onClear={handleClearFilters}
            />

            <div className="flex justify-end">
              <Can permission={ACTIONS.commissions.create}>
                <Button onClick={handleOpenCreate} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Nova Comissão
                </Button>
              </Can>
            </div>

            {list.loading && list.data.length === 0 ? (
              <div className="flex justify-center items-center py-16">
                <Spinner className="w-6 h-6" />
              </div>
            ) : list.error && list.data.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-16 text-center">
                <p className="text-destructive">{list.error}</p>
                <Button variant="outline" size="sm" onClick={list.reload}>
                  Tentar novamente
                </Button>
              </div>
            ) : list.data.length === 0 ? (
              <div className="py-16 text-center text-muted-foreground">
                Nenhuma comissão encontrada para os filtros aplicados.
              </div>
            ) : (
              <CommissionsTable
                commissions={list.data}
                loading={list.loading}
                currentPage={list.page}
                totalPages={list.totalPages}
                onEdit={handleOpenEdit}
                onDelete={handleOpenDelete}
                onPageChange={list.setPage}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {dialogContent === DIALOG_MODE.FORM && sellerId !== null && (
        <CommissionFormDialog
          open={dialogMode === DIALOG_MODE.FORM}
          loading={dialogLoading}
          lockedSellerId={sellerId}
          lockedSellerName={sellerName}
          commission={selected}
          onClose={closeDialog}
          onSubmit={handleFormSubmit}
        />
      )}
      {dialogContent === DIALOG_MODE.DELETE && (
        <CommissionDeleteDialog
          open={dialogMode === DIALOG_MODE.DELETE}
          loading={dialogLoading}
          commission={selected}
          onClose={closeDialog}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </>
  );
});
